import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const money = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.005) return "";
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const money0 = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AgingTable({ title, data, buckets }) {
  const cols = ["Party", ...buckets.map((b) => `${b} days`), "Total"];
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {cols.map((c, i) => (
                <TableCell key={c} align={i === 0 ? "left" : "right"} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.parties.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} sx={{ color: "text.secondary" }}>Nothing outstanding.</TableCell></TableRow>
            )}
            {data.parties.map((p) => (
              <TableRow key={p.party} hover>
                <TableCell sx={{ py: 0.4 }}>
                  {p.party}
                  {p.overLimit && (
                    <Box component="span" sx={{ ml: 1, px: 0.7, py: 0.1, fontSize: 11, fontWeight: 700, color: "#fff", bgcolor: "error.main", borderRadius: 1, whiteSpace: "nowrap" }}>
                      OVER LIMIT (${(Number(p.creditLimit) || 0).toLocaleString("en-US")})
                    </Box>
                  )}
                </TableCell>
                {p.buckets.map((v, i) => (
                  <TableCell key={i} align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums",
                    color: i === 3 && v > 0.005 ? "error.main" : undefined,
                    fontWeight: i === 3 && v > 0.005 ? 600 : 400 }}>
                    {money(v)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ py: 0.4, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{money(p.total)}</TableCell>
              </TableRow>
            ))}
            {data.parties.length > 0 && (
              <TableRow>
                <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800 }}>Total</TableCell>
                {data.totals.map((v, i) => (
                  <TableCell key={i} align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(v)}</TableCell>
                ))}
                <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 0.8, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(data.grandTotal)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function AgingReport() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!asOf) { setError("Pick a date"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/aging", { params: { asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Aging report"))
      .finally(() => setLoading(false));
  }, [asOf]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>A/R &amp; A/P Aging</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Outstanding invoices grouped by how overdue they are. Receivable = who owes you · Payable = who you owe.
        Aged from the invoice date; matches your Pending Invoices.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }}
            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>A/R &amp; A/P Aging</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <AgingTable title="Accounts Receivable — money owed to you" data={data.receivable} buckets={data.buckets} />
          <AgingTable title="Accounts Payable — money you owe" data={data.payable} buckets={data.buckets} />

          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            Open balance per invoice = invoice total − payments applied (through the date shown), the same as
            Pending Invoices. The <b>90+ days</b> column is highlighted — those are your oldest, highest-risk balances.
            Advances/credits held outside an invoice aren't netted here.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
